import { Logic } from '~common/ui-logic/logic'
import type { AppContextType } from '~/app-context'

export interface FileDropOverlayDeps extends AppContextType {}

export interface FileDropOverlayState {
    isDraggingFiles: boolean
    dragCounter: number
    processingVisible: boolean
    processingLabel?: string
    batchProcessing: boolean
    batchCurrentIndex: number
    batchTotalFiles: number
}

export class FileDropOverlayLogic extends Logic<
    FileDropOverlayDeps,
    FileDropOverlayState
> {
    private fileQueue: File[] = []
    private isProcessingQueue = false
    private processedCount = 0

    getInitialState(): FileDropOverlayState {
        return {
            isDraggingFiles: false,
            dragCounter: 0,
            processingVisible: false,
            processingLabel: undefined,
            batchProcessing: false,
            batchCurrentIndex: 0,
            batchTotalFiles: 0,
        }
    }

    initialize = async () => {}

    onGlobalDragEnter = (e: DragEvent) => {
        const hasFiles = Array.from(e.dataTransfer?.items || []).some(
            (it) => it.kind === 'file',
        )
        if (!hasFiles) return
        this.setState({
            isDraggingFiles: true,
            dragCounter: this.state.dragCounter + 1,
        })
    }

    onGlobalDragLeave = (_e: DragEvent) => {
        const next = this.state.dragCounter - 1
        const isDraggingFiles = next > 0
        this.setState({ dragCounter: next < 0 ? 0 : next, isDraggingFiles })
    }

    onGlobalDrop = () => {
        this.setState({ isDraggingFiles: false, dragCounter: 0 })
    }

    showProcessing = (label?: string) => {
        this.setState({ processingVisible: true, processingLabel: label })
    }

    hideProcessing = () => {
        this.setState({ processingVisible: false, processingLabel: undefined })
    }

    async processFiles(files: File[]): Promise<void> {
        if (files.length === 0) return

        this.fileQueue.push(...files)

        if (this.isProcessingQueue) {
            const totalFiles = this.processedCount + this.fileQueue.length + 1
            this.setState({
                batchProcessing: true,
                batchCurrentIndex: this.processedCount,
                batchTotalFiles: totalFiles,
                processingVisible: true,
            })
            return
        }

        await this.processQueue()
    }

    private async processQueue(): Promise<void> {
        if (this.fileQueue.length === 0) {
            this.isProcessingQueue = false
            this.processedCount = 0
            this.setState({
                batchProcessing: false,
                batchCurrentIndex: 0,
                batchTotalFiles: 0,
                processingVisible: false,
            })
            this.onGlobalDrop()
            return
        }

        this.isProcessingQueue = true

        while (this.fileQueue.length > 0) {
            const file = this.fileQueue.shift()
            if (!file) break

            const totalFiles = this.processedCount + this.fileQueue.length + 1
            this.setState({
                batchProcessing: true,
                batchCurrentIndex: this.processedCount,
                batchTotalFiles: totalFiles,
                processingVisible: true,
            })

            try {
                const invoice =
                    await this.deps.services.invoiceProcessor.processInvoiceFromFileUpload(
                        file,
                    )

                    console.log('invoice', invoice)
                if (invoice?.invoiceMetadata?.id) {
                    this.deps.services.transactionInvoiceMatching.findMatchesForInvoice(
                        invoice.invoiceMetadata.id,
                    )
                }
            } catch (error) {
                console.error(`Error processing file ${file.name}:`, error)
            } finally {
                this.processedCount++
            }
        }

        await this.processQueue()
    }
}

export default FileDropOverlayLogic
